/**
 * The pre-app boot screen: a framework-free overlay shown while migrations run
 * and for the states where the Vue app must NOT mount — a database newer than
 * the code, or a migration that failed. It renders straight into #app with
 * inline styles that read the theme tokens from style.css, so it looks right in
 * both themes and cannot itself depend on the schema it is about to change.
 */
import { errorMessage, translate } from './lib/i18n.js'
import { LANGUAGES, preferredLanguage, rememberLanguage } from './lib/languages.js'
import en from './locales/en.js'
import sr from './locales/sr.js'

const el = () => document.getElementById('app')

const CATALOGUES = { en, sr }

/** The app's words, in `language`, for the screens drawn before the app is. */
const say = (language) => (key, params) =>
  translate(key, params, { catalogue: CATALOGUES[language] ?? en, fallback: en, locale: language })

/** An error, in `language` (`errorMessage`): a known code translated, else as it came. */
export const sayError = (language) => (err) =>
  errorMessage(err, { catalogue: CATALOGUES[language] ?? en, fallback: en, locale: language })

/** The words of every screen here that has no language picker: this browser's. */
export const bootWords = () => say(preferredLanguage())
const words = bootWords

/**
 * OKANE's mark: the coin from the app icon (public/favicon.png), without the
 * tile behind it or the wordmark. The hole is cut out of it (a mask), so the
 * page shows through in either theme, as it does through a real coin.
 */
const COIN = `
  <svg id="okane-mark" width="56" height="56" viewBox="0 0 208 208" aria-hidden="true">
    <defs>
      <mask id="okane-mark-hole">
        <rect width="208" height="208" fill="#fff"/>
        <rect x="77" y="77" width="54" height="54" rx="6" fill="#000"/>
      </mask>
    </defs>
    <g mask="url(#okane-mark-hole)">
      <circle cx="104" cy="104" r="103.5" fill="#c18f2a"/>
      <circle cx="104" cy="104" r="83.5" fill="none" stroke="#9c7526" stroke-width="5.5"/>
    </g>
  </svg>`

/** `corner` sits in the page's top right — the sign-in screen's language picker. */
const shell = (inner, corner = '') => `
  <div style="position:relative;min-height:100dvh;display:grid;place-items:center;box-sizing:border-box;
              padding:calc(24px + env(safe-area-inset-top)) 24px calc(24px + env(safe-area-inset-bottom));
              background:var(--c-bg);color:var(--c-text);
              font-family:ui-sans-serif,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif">
    <div style="width:100%;max-width:22rem;text-align:center">
      <div style="display:flex;justify-content:center;margin-bottom:20px">${COIN}</div>
      ${inner}
    </div>
    ${corner ? `<div style="position:absolute;top:calc(16px + env(safe-area-inset-top));right:16px">${corner}</div>` : ''}
  </div>`

const bar = (pct) => `
  <div style="height:8px;border-radius:999px;background:var(--c-line);overflow:hidden;margin-top:20px">
    <div style="height:100%;width:${pct}%;border-radius:999px;background:var(--c-accent);
                transition:width .35s cubic-bezier(.22,1,.36,1)"></div>
  </div>`

const esc = (s) =>
  String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c])

/** Render the "updating your data" state, driven by runMigrations' onProgress. */
export function renderMigrating(event) {
  const total = event.total || 1
  // 'backup' sits before step 1; 'done' fills the bar.
  const done = event.phase === 'done' ? total : event.phase === 'migrating' ? event.index : 0
  const pct = Math.round((done / total) * 100)
  const t = words()
  const heading = event.phase === 'backup' ? t('boot.backingUp') : t('boot.updating')
  const line =
    event.phase === 'backup'
      ? t('boot.savingCopy')
      : event.phase === 'done'
        ? t('boot.almostThere')
        : `${t('boot.step', { n: event.index + 1, total })}${event.name ? ` · ${esc(event.name)}` : ''}`
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(heading)}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t('boot.awayWhile'))}</p>
    ${bar(pct)}
    <p style="font-size:12px;color:var(--c-faint);margin:12px 0 0">${line}</p>
  `)
}

/** The code is older than the database (a stale, cached bundle). */
export function renderAhead({ dbVersion, codeVersion, onReload }) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('boot.aheadTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t('boot.aheadLead', { app: codeVersion, data: dbVersion }))}</p>
    <button id="okane-reload" style="margin-top:20px;width:100%;padding:12px;border:0;border-radius:12px;
            font:inherit;font-weight:600;background:var(--c-accent);color:#04140d;cursor:pointer">
      ${esc(t('boot.reload'))}
    </button>
  `)
  document.getElementById('okane-reload')?.addEventListener('click', onReload)
}

/** A migration threw. Offer a retry and a way to rescue the pre-update backup. */
export function renderError({ message, canRestore, onRetry, onDownload }) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0;color:var(--c-danger)">${esc(t('boot.errorTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t(canRestore ? 'boot.errorLeadBackup' : 'boot.errorLead'))}</p>
    <code style="display:block;margin-top:14px;padding:10px;border-radius:10px;background:var(--c-raised);
           color:var(--c-faint);font-size:11px;text-align:left;overflow-wrap:anywhere">${esc(message)}</code>
    <button id="okane-retry" style="margin-top:16px;width:100%;padding:12px;border:0;border-radius:12px;
            font:inherit;font-weight:600;background:var(--c-accent);color:#04140d;cursor:pointer">
      ${esc(t('boot.tryAgain'))}
    </button>
    ${
      canRestore
        ? `<button id="okane-download" style="margin-top:8px;width:100%;padding:12px;border:1px solid var(--c-line);
             border-radius:12px;font:inherit;font-weight:600;background:transparent;color:var(--c-text);cursor:pointer">
             ${esc(t('boot.downloadBackup'))}
           </button>`
        : ''
    }
  `)
  document.getElementById('okane-retry')?.addEventListener('click', onRetry)
  document.getElementById('okane-download')?.addEventListener('click', onDownload)
}

/**
 * Another tab or window has the database open. The file on disk is held by one
 * tab at a time — two editing it would each overwrite the other — so this one
 * waits its turn instead of opening a second, diverging copy.
 */
export function renderLocked({ onRetry }) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('boot.lockedTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t('boot.lockedLead'))}</p>
    <button id="okane-retry" style="margin-top:20px;width:100%;padding:12px;border:0;border-radius:12px;
            font:inherit;font-weight:600;background:var(--c-accent);color:#04140d;cursor:pointer">
      ${esc(t('boot.tryAgain'))}
    </button>
  `)
  document.getElementById('okane-retry')?.addEventListener('click', onRetry)
}

/**
 * Cloud mode, signed out: the way in. A magic link (typed address, e-mailed
 * link that opens the app again) or a passkey — and a way back to keeping the
 * data on this device instead. Plain DOM like the rest of this file, in the
 * language this browser prefers (lib/languages.js) — there is no database yet
 * to hold the setting — with a picker in the corner to change it.
 */
export function renderSignIn(options) {
  const { message = '', passkeys, moving = false, onLink, onPasskey, onLocal } = options
  // Picked here, or in the app before, or the browser's own.
  const language = options.language ?? preferredLanguage()
  const t = say(language)
  if (typeof document !== 'undefined' && document.documentElement) document.documentElement.lang = language
  const button = (id, label, primary) => `
    <button id="${id}" type="${primary ? 'submit' : 'button'}" style="margin-top:10px;width:100%;padding:12px;
            border-radius:12px;font:inherit;font-weight:600;cursor:pointer;
            ${primary ? 'border:0;background:var(--c-accent);color:#04140d' : 'border:1px solid var(--c-line);background:transparent;color:var(--c-text)'}">
      ${esc(label)}
    </button>`
  const picker = `
    <select id="okane-language" aria-label="${esc(t('signin.language'))}"
            style="padding:7px 10px;border-radius:10px;border:1px solid var(--c-line);background:var(--c-raised);
                   color:var(--c-text);font:inherit;font-size:13px;cursor:pointer">
      ${LANGUAGES.map((l) => `<option value="${l.code}"${l.code === language ? ' selected' : ''}>${esc(l.label)}</option>`).join('')}
    </select>`
  el().innerHTML = shell(
    `
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('signin.title'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 18px">${esc(t('signin.lead'))}</p>
    ${
      moving
        ? `<p id="okane-moving" style="font-size:12.5px;color:var(--c-muted);margin:-8px 0 18px;padding:10px;
                border-radius:12px;background:var(--c-raised)">${esc(t('signin.moving'))}</p>`
        : ''
    }
    <form id="okane-signin" style="margin:0">
      <label for="okane-email" style="display:block;text-align:left;font-size:12px;color:var(--c-muted);margin-bottom:6px">${esc(t('signin.email'))}</label>
      <input id="okane-email" type="email" autocomplete="email webauthn" required
             value="${esc(options.email ?? '')}"
             style="width:100%;box-sizing:border-box;padding:12px;border-radius:12px;border:1px solid var(--c-line);
                    background:var(--c-raised);color:var(--c-text);font:inherit" />
      ${button('okane-link', t('signin.sendLink'), true)}
    </form>
    ${passkeys ? button('okane-passkey', t('signin.passkey'), false) : ''}
    <div style="display:flex;gap:8px">
      ${options.onShowCode ? button('okane-show-code', t('signin.showCode'), false) : ''}
      ${options.onScan && options.canScan ? button('okane-scan-code', t('signin.scanCode'), false) : ''}
    </div>
    <p id="okane-status" role="status" style="min-height:1.4em;font-size:13px;color:var(--c-muted);margin:14px 0 0">${esc(message)}</p>
    <button id="okane-local" type="button" style="margin-top:14px;border:0;background:none;color:var(--c-faint);
            font:inherit;font-size:12.5px;text-decoration:underline;cursor:pointer">
      ${esc(t('signin.local'))}
    </button>
  `,
    picker,
  )
  const status = document.getElementById('okane-status')
  const tell = (text, tone) => {
    status.textContent = text
    status.style.color = tone === 'bad' ? 'var(--c-danger)' : tone === 'good' ? 'var(--c-accent)' : 'var(--c-muted)'
  }
  const busy = (on) => {
    for (const id of ['okane-link', 'okane-passkey']) {
      const b = document.getElementById(id)
      if (b) b.disabled = on
    }
  }
  // Another language redraws the screen in it, keeping what was typed; the
  // browser remembers the choice for the app, and for next time.
  document.getElementById('okane-language')?.addEventListener('change', (event) => {
    const code = event.target.value
    rememberLanguage(code)
    renderSignIn({ ...options, language: code, message: '', email: document.getElementById('okane-email')?.value ?? '' })
  })
  document.getElementById('okane-signin')?.addEventListener('submit', async (event) => {
    event.preventDefault()
    const email = document.getElementById('okane-email').value.trim()
    busy(true)
    try {
      await onLink(email, language)
      tell(t('signin.sent', { email }), 'good')
    } catch (err) {
      tell(sayError(language)(err), 'bad')
    } finally {
      busy(false)
    }
  })
  document.getElementById('okane-passkey')?.addEventListener('click', async () => {
    busy(true)
    try {
      await onPasskey()
    } catch (err) {
      // Dismissing the browser's prompt is not an error worth shouting about.
      tell(err?.name === 'NotAllowedError' ? t('signin.noPasskey') : sayError(language)(err), 'bad')
      busy(false)
    }
  })
  document.getElementById('okane-local')?.addEventListener('click', onLocal)
  document.getElementById('okane-show-code')?.addEventListener('click', () => options.onShowCode(language))
  document.getElementById('okane-scan-code')?.addEventListener('click', () => options.onScan(language))
}

const secondary = (id, label) => `
  <button id="${id}" type="button" style="margin-top:14px;width:100%;padding:12px;border-radius:12px;font:inherit;
          font-weight:600;cursor:pointer;border:1px solid var(--c-line);background:transparent;color:var(--c-text)">
    ${esc(label)}
  </button>`

/**
 * Signing in from the phone (tenantdb's api/devices.go, direction 2): this
 * device shows a code, the signed-in phone scans it, and the two screens show
 * the same six digits to be compared before the phone approves. `svg` is the
 * code (src/camera.js); `state` is waiting or expired.
 */
export function renderShowCode({ language = preferredLanguage(), svg = '', code = '', state = 'waiting', onBack, onRenew }) {
  const t = say(language)
  const digits = `${code.slice(0, 3)} ${code.slice(3)}`
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('device.showTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 16px">${esc(t('device.showLead'))}</p>
    <div id="okane-code" style="width:220px;max-width:100%;margin:0 auto;border-radius:14px;overflow:hidden;
         ${state === 'expired' ? 'opacity:.25;' : ''}">${svg}</div>
    <p id="okane-digits" style="font-size:26px;font-weight:700;letter-spacing:.12em;margin:16px 0 4px;
       font-variant-numeric:tabular-nums">${esc(digits)}</p>
    <p style="font-size:12.5px;color:var(--c-muted);margin:0">${esc(t('device.compare'))}</p>
    <p id="okane-status" role="status" style="font-size:13px;margin:14px 0 0;color:${state === 'expired' ? 'var(--c-danger)' : 'var(--c-muted)'}">
      ${esc(state === 'expired' ? t('device.expired') : t('device.waiting'))}
    </p>
    ${state === 'expired' ? secondary('okane-renew', t('device.newCode')) : ''}
    ${secondary('okane-back', t('device.back'))}
  `)
  document.getElementById('okane-back')?.addEventListener('click', onBack)
  document.getElementById('okane-renew')?.addEventListener('click', onRenew)
}

/**
 * Signing this phone in from a device that already is (direction 1): the
 * camera, pointed at the code that device shows. Returns the `<video>` for the
 * scanner, and `say(key, params, tone)` / `tell(text, tone)` for what it finds.
 */
export function renderScan({ language = preferredLanguage(), onBack }) {
  const t = say(language)
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('device.scanTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 16px">${esc(t('device.scanLead'))}</p>
    <div style="position:relative;width:100%;aspect-ratio:1;border-radius:16px;overflow:hidden;background:#000">
      <video id="okane-camera" playsinline muted style="width:100%;height:100%;object-fit:cover"></video>
      <div style="position:absolute;inset:18%;border:3px solid rgba(255,255,255,.85);border-radius:14px"></div>
    </div>
    <p id="okane-status" role="status" style="min-height:1.4em;font-size:13px;color:var(--c-muted);margin:14px 0 0"></p>
    ${secondary('okane-back', t('device.back'))}
  `)
  document.getElementById('okane-back')?.addEventListener('click', onBack)
  const status = document.getElementById('okane-status')
  const tell = (text, tone) => {
    status.textContent = text
    status.style.color = tone === 'bad' ? 'var(--c-danger)' : 'var(--c-muted)'
  }
  return {
    video: document.getElementById('okane-camera'),
    /** A message from the catalogue, in this screen's language. */
    say: (key, params, tone) => tell(t(key, params), tone),
    /** Words that arrive already written — the server's. */
    tell,
  }
}

/** A move between this device and the cloud is under way (`db/move.js`). */
/** `files`: `{ done, total }` while its files are on the way. */
export function renderMoving({ to, files = null }) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t(to === 'cloud' ? 'boot.movingCloud' : 'boot.movingDevice'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t('boot.movingLead'))}</p>
    ${files ? `<p id="okane-moving-files" style="font-size:12.5px;color:var(--c-muted);margin:8px 0 0">${esc(t('states.files', { done: files.done, total: files.total }))}</p>` : ''}
    ${bar(files?.total ? Math.round((files.done / files.total) * 100) : 60)}
  `)
}

/**
 * A move stopped before anything was destroyed: the ledger is still where it
 * was. Try again, or give up and keep it there.
 */
export function renderMoveFailed({ message, onRetry, onStay }) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0;color:var(--c-danger)">${esc(t('boot.moveFailedTitle'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 0">${esc(t('boot.moveFailedLead'))}</p>
    <code style="display:block;margin-top:14px;padding:10px;border-radius:10px;background:var(--c-raised);
           color:var(--c-faint);font-size:11px;text-align:left;overflow-wrap:anywhere">${esc(message)}</code>
    <button id="okane-retry" style="margin-top:16px;width:100%;padding:12px;border:0;border-radius:12px;
            font:inherit;font-weight:600;background:var(--c-accent);color:#04140d;cursor:pointer">
      ${esc(t('boot.tryAgain'))}
    </button>
    <button id="okane-stay" style="margin-top:8px;width:100%;padding:12px;border:1px solid var(--c-line);
            border-radius:12px;font:inherit;font-weight:600;background:transparent;color:var(--c-text);cursor:pointer">
      ${esc(t('boot.keepHere'))}
    </button>
  `)
  document.getElementById('okane-retry')?.addEventListener('click', onRetry)
  document.getElementById('okane-stay')?.addEventListener('click', onStay)
}

export function renderFatal(message) {
  const t = words()
  el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0;color:var(--c-danger)">${esc(t('boot.fatalTitle'))}</h1>
    <code style="display:block;margin-top:14px;padding:10px;border-radius:10px;background:var(--c-raised);
           color:var(--c-faint);font-size:11px;text-align:left;overflow-wrap:anywhere">${esc(message)}</code>
  `)
}

/**
 * The lock of a ledger encrypted on this device (stores/sealed.js): nothing can
 * open until the passphrase — or the recovery key — does. `onUnlock(secret)`
 * resolves once it opened, or rejects `wrong-passphrase` / `wrong-recovery-key`,
 * which is said here and the screen stays.
 */
export function renderUnlock({ onUnlock, message = '' }) {
  const language = preferredLanguage()
  const t = say(language)
  let recovery = false
  const draw = () => {
    el().innerHTML = shell(`
    <h1 style="font-size:18px;font-weight:600;margin:0">${esc(t('unlock.title'))}</h1>
    <p style="font-size:13px;color:var(--c-muted);margin:8px 0 18px">${esc(t('unlock.lead'))}</p>
    <form id="okane-unlock" style="margin:0">
      <input id="okane-secret" type="${recovery ? 'text' : 'password'}" autocomplete="${recovery ? 'off' : 'current-password'}" required autofocus
             aria-label="${esc(recovery ? t('sync.recoveryPlaceholder') : t('sync.passphrase'))}"
             placeholder="${esc(recovery ? t('sync.recoveryPlaceholder') : t('sync.passphrase'))}"
             style="width:100%;box-sizing:border-box;padding:12px;border-radius:12px;border:1px solid var(--c-line);
                    background:var(--c-raised);color:var(--c-text);font:inherit" />
      <button id="okane-open" type="submit" style="margin-top:10px;width:100%;padding:12px;border-radius:12px;font:inherit;
              font-weight:600;cursor:pointer;border:0;background:var(--c-accent);color:#04140d">${esc(t('unlock.open'))}</button>
    </form>
    <p id="okane-status" role="status" style="min-height:1.4em;font-size:13px;color:var(--c-danger);margin:14px 0 0">${esc(message)}</p>
    <button id="okane-which" type="button" style="margin-top:6px;border:0;background:none;color:var(--c-faint);
            font:inherit;font-size:12.5px;text-decoration:underline;cursor:pointer">
      ${esc(recovery ? t('sync.usePassphrase') : t('sync.useRecovery'))}
    </button>`)
    document.getElementById('okane-which').addEventListener('click', () => {
      recovery = !recovery
      message = ''
      draw()
    })
    document.getElementById('okane-unlock').addEventListener('submit', async (event) => {
      event.preventDefault()
      const secret = document.getElementById('okane-secret').value
      const button = document.getElementById('okane-open')
      button.disabled = true
      button.textContent = t('gate.opening')
      try {
        await onUnlock(recovery ? { recovery: secret } : { passphrase: secret })
      } catch (err) {
        message =
          err?.code === 'wrong-passphrase'
            ? t('sync.wrongPassphrase')
            : err?.code === 'wrong-recovery-key' || err?.code === 'bad-recovery-key'
              ? t('sync.wrongRecovery')
              : sayError(language)(err)
        draw()
      }
    })
    document.getElementById('okane-secret')?.focus()
  }
  draw()
}
